﻿import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ValidationButton from '../../../Forms/Components/ValidationButton';
import RadioButton from '../../../Forms/Components/RadioButton';
import {ActionButton} from '../../../UI/pageComponents';

import Form from '../../../../vendor/react-validation/components/form';
import {required} from '../../../Forms/Validation/rules';
import getFormValues from '../../../Forms/getFormValues';

import {getInsurancePurchaseUrlForAction} from '../../../State/actions';
import {getRelatedGroupsForInsurance} from '../../../DataHelpers/lfDataHelpers'; 

const chunk = (arr, n) => 
    Array(Math.ceil(arr.length/n)).fill().map((_,i) => arr.slice(i*n,i*n+n));

import {iconCode, mediaQuery, clearFix} from '../../../Style/cssHelpers';
import definitions from '../../../Style/LfStyleDefinitions';

const minNoOfUpgradesForLargeView = 2; 

class UpgradeCta extends React.Component {
    static get propTypes() {
        return {
            alignment: PropTypes.string,
            possession: PropTypes.object.isRequired
        };
    }

    constructor(props) {
        super(props);
        
        this.form = null;
        this.validationTimer = null; 
    }

    handleFormSelection = (e) => {
        e.preventDefault();
        const {upgradeUrl} = getFormValues(this.form);
        window.location = upgradeUrl; 
    }

    render() {
        const possession = this.props.possession;
        const possessionDisplayName = this.props.data.insurancesByName[possession.insuranceName].displayNameHeading;
        const upgrades = this.props.data.insurancesByName[possession.insuranceName].possibleUpgrades
            .filter(upgradeName => (this.props.data.quotesByName[upgradeName]))
            .map(upgradeName => ({
                ...this.props.data.insurancesByName[upgradeName], 
                ...this.props.data.quotesByName[upgradeName]
            }));

        const isMinQuote = upgrades.length == 1 ? upgrades[0].isMinQuote : true;

        const isPregnantOrChildInsurance = upgrades.find(u => u.insuranceName.match(/^(pregnant|child)\-/) != null);
        const smallUpgradeCTA = isPregnantOrChildInsurance && upgrades.length < minNoOfUpgradesForLargeView;

        if (smallUpgradeCTA) { // small upgrade selection
            return (
                <Wrapper alignment={this.props.alignment}>
                    <UpgradeHeading>Uppgradera din {possessionDisplayName}{isMinQuote && " från"}: </UpgradeHeading>
                        {upgrades.map((upgrade, i) =>
                            <a 
                                href={this.props.getInsurancePurchaseUrlFor([upgrade.groupName, ...getRelatedGroupsForInsurance(upgrade.insuranceName)], upgrade.insuranceName, possession.possessionId)}
                                key={`upgrade-${upgrade.groupName}-${i}`}
                            >
                                <ActionButton>
                                    +{upgrade.quote - possession.premium} kr{upgrade.premiumPeriod == "monthly" ? "/mån" : " i engångspris"}
                                </ActionButton>
                            </a>
                        )}
                </Wrapper>
            );
        } else { // large upgrade selection
            return (
                <LargeUpgradeWrapper>
                    <UpgradeHeading>Uppgradera din {possessionDisplayName} till: </UpgradeHeading>
                    <Form ref={el => { this.form = el; }} onSubmit={this.handleFormSelection}>
                        <GridContainer>
                            {chunk(upgrades, 3).map((chunkedUpgrades, i) => (
                                <Column key={`large-outer-${chunkedUpgrades[0].insuranceName}`}>
                                    {chunkedUpgrades.map((upgrade, j) => {
                                        return (
                                            <PossessionRow key={`large-inner-${upgrade.insuranceName}`}>
                                                <PossessionColumn>
                                                    <RadioButton 
                                                        layout="block"
                                                        textColor="white"
                                                        errorColor={definitions.colors.lightError}
                                                        id={upgrade.insuranceName}
                                                        name="upgradeUrl"
                                                        value={this.props.getInsurancePurchaseUrlFor(upgrade.groupName, upgrade.insuranceName, upgrade.possessionId)}
                                                        validations={[required]}
                                                        displayname={upgrade.displayName}
                                                        // show error msg only on last radio item
                                                        data-validation-error-message={(i+1)*(j+1) === upgrades.length - 1 ? 'Välj en uppgradering' : ''}
                                                        customwidth="100%"
                                                    />
                                                </PossessionColumn>
                                                <PriceColumn>
                                                    +{upgrade.quote - possession.premium} kr{upgrade.premiumPeriod == "monthly" ? "/mån" : " i engångspris"}
                                                </PriceColumn>
                                            </PossessionRow>
                                        );
                                    })}
                                </Column>
                            ))}
                        </GridContainer>
                        <ValidationButtonWrapper>
                            <ValidationButton 
                                onMouseEnter={() => this.validationTimer = setTimeout(this.form.validateAll, 1000)}
                                onMouseLeave={() => clearTimeout(this.validationTimer)}
                                topMargin="20px"
                                noIcon
                            >
                                Uppgradera
                            </ValidationButton>
                        </ValidationButtonWrapper>
                    </Form>
                </LargeUpgradeWrapper>
            );
        }
    }
}

// state

const mapStateToProps = (state) => {
    return {
        data: state.startPageData,
        metaData: state.startPageInsuranceMetaData
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getInsurancePurchaseUrlFor: (groupName, selection, possessionId) => dispatch(getInsurancePurchaseUrlForAction(groupName, selection, possessionId))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(UpgradeCta);

// styles

const Wrapper = styled.div`
    display: block; 
    text-align: ${props => props.alignment ? props.alignment : 'center'}; 
    margin-bottom: 55px;

    ${mediaQuery.medium(`
        margin-bottom: 0px;
    `)}
    ${mediaQuery.xsmall(`
        max-width: none;
    `)}
    ${mediaQuery.xxsmall(`
        max-width: 88vw;
    `)}
`;

const UpgradeHeading = styled.h4`
    display: inline-block;
    margin-right: 40px;
    margin-bottom: 20px;
    font-size: 20px;
    color: white;
    ${mediaQuery.medium(`
        margin-top: 25px;
        width: 100%;
    `)}
    ${mediaQuery.small(`
        font-size: 16px;
        float: left;
        margin-bottom: 15px;
        margin-right: 0px;
        text-align: left;
    `)}
`;

const LargeUpgradeWrapper = styled.div`
    padding: 30px;

    ${mediaQuery.medium(`
        padding: 15px; 
    `)}

    ${mediaQuery.small(`
        padding: 0px; 
    `)}
`;

const GridContainer = styled.div`
    position: relative; 
    width: 100%; 

    ${clearFix()}
`;

const Column = styled.div`
    width: 50%; 
    float: left; 
    ${mediaQuery.medium(`
        width: 100%; 
        float: none; 
    `)}

    ${clearFix()}
`;

const PossessionRow = styled.div`
    position: relative; 
    
    width: 100%; 
    margin-top: 15px; 
    line-height: 1.5;

    ${clearFix()}
`;

const PossessionColumn = styled.div`
    position: relative; 
    width: 70%; 
    float: left;
`;

const PriceColumn = styled.div`
    position: relative; 
    width: 30%; 
    float: left;

    padding-right: 30px;

    font-size: 16px;
    color: white; 
    text-align: right;

    line-height: 1.2;

    ${mediaQuery.medium(`
        padding-right: 0;
        line-height: 1.8; 
    `)}

    ${mediaQuery.small(`
        font-size: 13px;
        line-height: 2.2;
    `)}
`;

const ValidationButtonWrapper = styled.div`
    text-align: center;
`;

